import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useMemo } from 'react';
import MuiBox from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import MuiTypography from '@mui/material/Typography';
import OverflowWindow from '../OverflowWindow/OverflowWindow.js';
import '../OverflowWindow/OverflowProvider.js';
import '../OverflowWindow/components/MenuCategory.js';
import '../OverflowWindow/components/ChecklistItems.js';
import '../OverflowWindow/components/MenuItem.js';
import '../OverflowWindow/components/SubMenuItem.js';
import '../OverflowWindow/components/CascadingProvider.js';
import { ReactComponent as ForwardRef$1 } from '../../assets/icons/ArrowDownSmall.svg.js';
import { ReactComponent as ForwardRef } from '../../assets/icons/ArrowUpSmall.svg.js';
import Hint from '../Hint/Hint.js';

const schemeColorMap = {
    gray: 'main',
    blue: 'main',
    green: 'main',
    yellow: 'main',
    red: 'main',
    cyan: '600',
};
const schemeMap = {
    gray: 'N10',
    blue: 'primary',
    green: 'success',
    yellow: 'warning',
    red: 'error',
    cyan: 'UCyan',
};
const Label = styled(MuiTypography, {
    shouldForwardProp: prop => !['iconPosition', 'textStyle'].includes(prop),
}) `
  ${({ theme, size, icon, iconPosition = 'right', textStyle }) => {
    let padding = '0';
    if (size === 'large' && icon) {
        padding = theme.spacing(0, iconPosition === 'right' ? 2 : 0, 0, iconPosition === 'left' ? 2 : 0);
    }
    return `
      padding: ${padding};
      ${size === 'small' ? `font-size: 10px;` : ''}
      text-transform: ${textStyle}
    `;
}}
`;
function isPaletteColor(color) {
    return color.main !== undefined;
}
const generateColors = (theme, scheme, type, customColor) => {
    const paletteColor = theme.palette[schemeMap[scheme]];
    let color;
    if (customColor && /^#[0-9A-F]{6}$/i.test(customColor)) {
        color = customColor;
    }
    else if (isPaletteColor(paletteColor)) {
        color = paletteColor[schemeColorMap[scheme]];
    }
    else {
        color = paletteColor[schemeColorMap[scheme]];
    }
    let textColor = color;
    let backgroundColor = alpha(color, 0.2);
    if (type !== 'transparent') {
        if (type !== 'ghost') {
            textColor = theme.palette[type === 'fill' ? 'N90' : 'N30'].main;
        }
        backgroundColor = type === 'fill' ? color : 'transparent';
    }
    return { textColor, backgroundColor };
};
const Root = styled(MuiBox, {
    shouldForwardProp: prop => !['scheme', 'type', 'hasMenu', 'menuOpen', 'fullWidth', 'customColor', 'withHover'].includes(prop),
}) `
  ${({ scheme = 'gray', type = 'transparent', hasMenu, menuOpen, size = 'medium', theme, fullWidth, customColor, withHover = false, }) => {
    const { backgroundColor, textColor } = generateColors(theme, scheme, type, customColor);
    let padding = theme.spacing(1.25, hasMenu ? 0.5 : 1.5, 1.25, 1.5);
    if (size === 'large') {
        padding = theme.spacing(0.5, 3, 0.5, 4);
    }
    else if (size === 'small') {
        padding = theme.spacing(0, 0.75, 0, 0.75);
    }
    const width = fullWidth ? '100%' : 'fit-content';
    return `
      color: ${textColor};
      background-color: ${backgroundColor};
      height: ${size === 'large' ? 30 : 18}px;
      line-height: 15px;
      padding: ${padding};
      border-radius: 4px;
      border: ${type === 'ghost' ? `1px solid ${textColor}` : 'none'};
      width: ${width};
      position: relative;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;

      > h6 {
        line-height: 2.57;
      }

      :before {
        border-radius: 4px;
        background-color: transparent;
        transition: border ${theme.transitions.duration.fast} linear;
        border: 1px solid ${menuOpen && hasMenu && type === 'transparent' ? textColor : 'transparent'};
        content: "";
        display: block;
        position: absolute;
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
      }
      
      ${withHover && `&:hover { filter: brightness(120%); }`};      
    `;
}}
`;
const PillStatus = (_a) => {
    var { scheme = 'gray', type = 'transparent', label, children, hint, hintDuration, disabled, size = 'medium', icon, iconPosition = 'left', textStyle = 'uppercase', 'data-testid': testId } = _a, rest = __rest(_a, ["scheme", "type", "label", "children", "hint", "hintDuration", "disabled", "size", "icon", "iconPosition", "textStyle", 'data-testid']);
    const [menuOpen, setMenuOpen] = useState(false);
    const hasMenu = useMemo(() => !!children && size === 'medium', [children, size]);
    const hasIcon = useMemo(() => size === 'large' && !!icon, [icon, size]);
    const title = hint ? jsx("div", Object.assign({ style: { whiteSpace: 'pre-line' } }, { children: hint })) : null;
    return (jsx(OverflowWindow, Object.assign({ dense: true, onToggle: setMenuOpen, "data-testid": "pill-status-menu", target: jsx(Hint, Object.assign({ title: title, duration: hintDuration, placement: "bottom-end", disabled: disabled }, { children: jsxs(Root, Object.assign({ "data-testid": testId !== null && testId !== void 0 ? testId : 'pill-status', scheme: scheme, type: type, hasMenu: hasMenu, menuOpen: menuOpen, size: size }, rest, { children: [hasIcon && iconPosition === 'left' && icon, jsx(Label, Object.assign({ noWrap: true, iconPosition: iconPosition, size: size, icon: icon, variant: "subtitle2", textStyle: textStyle }, { children: label })), hasMenu && (jsx(Fragment, { children: menuOpen ? jsx(ForwardRef, { "data-testid": "menu-arrow" }) : jsx(ForwardRef$1, { "data-testid": "menu-arrow" }) })), hasIcon && iconPosition === 'right' && icon] })) })) }, { children: children })));
};

export { PillStatus as default, generateColors };
//# sourceMappingURL=PillStatus.js.map
